import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Zap, ArrowRight, Sparkles, Mail, Brain, Clock, Shield, ChevronRight,
  FolderOpen, Users, MessageSquare, PenTool, Check, Star, FileText, TrendingUp,
  Play, X
} from 'lucide-react';
import { Language, landingTranslations } from '../locales/landing';
import LivingStructureBackground from '../components/landing/LivingStructureBackground';

interface LandingLinear2Props {
  lang?: Language;
}

// Tilted Screenshot Component with 3D Room Effect - Like Linear
const TiltedScreenshot: React.FC = () => {
  return (
    <div className="relative w-full overflow-visible" style={{ perspective: '1200px' }}>
      {/* Glow effect behind */}
      <div className="absolute inset-0 -top-40 bg-gradient-to-b from-violet-500/15 via-violet-500/5 to-transparent blur-3xl" />
      
      {/* Screenshot container - Tilted like Linear (right side goes back) */}
      <motion.div 
        className="relative w-[1500px] ml-[5%]"
        initial={{ opacity: 0, y: 120, rotateX: 55, rotateY: 15, rotateZ: 2 }}
        animate={{ opacity: 1, y: 0, rotateX: 45, rotateY: 12, rotateZ: 1 }}
        transition={{ duration: 1.6, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
        style={{ 
          transformStyle: 'preserve-3d',
          transformOrigin: 'left center',
        }}
      >
        {/* Main screenshot */}
        <div className="rounded-xl overflow-hidden border border-white/10 bg-[#0a0a0c] shadow-2xl shadow-black/60">
          {/* Browser header */}
          <div className="flex items-center gap-2 px-4 py-3 border-b border-white/5 bg-[#0c0c0e]">
            <div className="flex gap-1.5">
              <div className="w-3 h-3 rounded-full bg-[#ff5f57]" />
              <div className="w-3 h-3 rounded-full bg-[#febc2e]" />
              <div className="w-3 h-3 rounded-full bg-[#28c840]" />
            </div>
            <div className="flex-1 flex justify-center">
              <div className="px-4 py-1.5 rounded-lg bg-white/5 text-white/40 text-xs flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-emerald-500/50" />
                app.lexy.no/inbox
              </div>
            </div>
            <div className="w-16" />
          </div>
          
          {/* App content - Inbox with AI Analysis */}
          <div className="flex min-h-[500px]">
            {/* Sidebar */}
            <div className="w-64 border-r border-white/5 bg-[#0a0a0c] p-4">
              <div className="flex items-center gap-2.5 mb-8 px-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                  <Zap className="w-4 h-4 text-white" />
                </div>
                <span className="text-sm font-medium text-white">Lexy</span>
              </div>
              
              <div className="space-y-1">
                <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-white/5 text-white text-sm">
                  <Mail className="w-4 h-4 text-white/60" />
                  <span>Innboks</span>
                  <span className="ml-auto text-[11px] bg-violet-500 text-white px-2 py-0.5 rounded-full font-medium">12</span>
                </div>
                <div className="flex items-center gap-3 px-3 py-2.5 text-white/40 text-sm hover:bg-white/5 rounded-lg transition-colors">
                  <Sparkles className="w-4 h-4" />
                  <span>AI Assistent</span>
                </div>
                <div className="flex items-center gap-3 px-3 py-2.5 text-white/40 text-sm hover:bg-white/5 rounded-lg transition-colors">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
                  </svg>
                  <span>Saker</span>
                </div>
                <div className="flex items-center gap-3 px-3 py-2.5 text-white/40 text-sm hover:bg-white/5 rounded-lg transition-colors">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                  </svg>
                  <span>Dokumenter</span>
                </div>
              </div>
            </div>
            
            {/* Main content - Email list */}
            <div className="flex-1 p-6 bg-[#09090b]">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-base font-medium text-white">Innkommende e-poster</h2>
                <div className="flex items-center gap-2 text-xs text-white/40">
                  <span>Sorter etter:</span>
                  <span className="text-white/60">Nyeste</span>
                </div>
              </div>
              
              {/* Email items */}
              <div className="space-y-2">
                {/* Email 1 - With AI badge */}
                <div className="p-4 rounded-xl bg-white/[0.03] border border-white/5 hover:border-violet-500/30 hover:bg-violet-500/5 transition-all cursor-pointer">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-sm font-medium">
                        AB
                      </div>
                      <div>
                        <p className="text-sm text-white font-medium">Advokatfirma Berg AS</p>
                        <p className="text-xs text-white/40">Angående fusjon - due diligence dokumenter</p>
                      </div>
                    </div>
                    <span className="text-xs text-white/30">10:42</span>
                  </div>
                  <div className="flex items-center gap-2 ml-13">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-violet-500/20 text-violet-300 text-[11px] font-medium">
                      <Brain className="w-3 h-3" />
                      AI analysert
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/20 text-amber-300 text-[11px] font-medium">
                      <Clock className="w-3 h-3" />
                      Haster
                    </span>
                  </div>
                </div>
                
                {/* Email 2 */}
                <div className="p-4 rounded-xl bg-white/[0.03] border border-white/5 hover:border-white/10 transition-all cursor-pointer">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center text-white text-sm font-medium">
                        OT
                      </div>
                      <div>
                        <p className="text-sm text-white font-medium">Oslo Tingrett</p>
                        <p className="text-xs text-white/40">Innkalling til hovedforhandling - Sak 23-123456</p>
                      </div>
                    </div>
                    <span className="text-xs text-white/30">09:15</span>
                  </div>
                  <div className="flex items-center gap-2 ml-13">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-violet-500/20 text-violet-300 text-[11px] font-medium">
                      <Brain className="w-3 h-3" />
                      AI analysert
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-[11px] font-medium">
                      <Shield className="w-3 h-3" />
                      Viktig
                    </span>
                  </div>
                </div>
                
                {/* Email 3 */}
                <div className="p-4 rounded-xl bg-white/[0.03] border border-white/5 hover:border-white/10 transition-all cursor-pointer">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center text-white text-sm font-medium">
                        HE
                      </div>
                      <div>
                        <p className="text-sm text-white/70">Hansen Eiendom AS</p>
                        <p className="text-xs text-white/30">Spørsmål om kontraktsforhold ved salg</p>
                      </div>
                    </div>
                    <span className="text-xs text-white/30">I går</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* AI Analysis Panel */}
            <div className="w-80 border-l border-white/5 bg-[#0a0a0c] p-6">
              <div className="flex items-center gap-2.5 mb-6">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <span className="text-sm font-medium text-white">AI Analyse</span>
              </div>
              
              <div className="space-y-4">
                <div className="p-4 rounded-xl bg-violet-500/10 border border-violet-500/20">
                  <p className="text-xs text-violet-300 font-medium mb-2">Sammendrag</p>
                  <p className="text-sm text-white/60 leading-relaxed">
                    Due diligence forespørsel for planlagt fusjon. Frist for dokumentlevering: 15. januar 2025.
                  </p>
                </div>
                
                <div className="p-4 rounded-xl bg-white/5 border border-white/5">
                  <p className="text-xs text-white/60 font-medium mb-3">Foreslåtte handlinger</p>
                  <ul className="space-y-2">
                    <li className="text-sm text-white/50 flex items-center gap-2.5 hover:text-white/70 cursor-pointer transition-colors">
                      <span className="w-1.5 h-1.5 rounded-full bg-violet-400" />
                      Opprett ny saksmappe
                    </li>
                    <li className="text-sm text-white/50 flex items-center gap-2.5 hover:text-white/70 cursor-pointer transition-colors">
                      <span className="w-1.5 h-1.5 rounded-full bg-violet-400" />
                      Send til dokumenthåndtering
                    </li>
                    <li className="text-sm text-white/50 flex items-center gap-2.5 hover:text-white/70 cursor-pointer transition-colors">
                      <span className="w-1.5 h-1.5 rounded-full bg-violet-400" />
                      Legg frist til i kalender
                    </li>
                  </ul>
                </div>
                
                <button className="w-full py-2.5 rounded-lg bg-violet-500 hover:bg-violet-600 text-white text-sm font-medium transition-colors">
                  Utfør alle handlinger
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Bottom fade/gradient for depth */}
        <div className="absolute -bottom-32 left-0 right-0 h-32 bg-gradient-to-t from-[#08080a] via-[#08080a]/80 to-transparent pointer-events-none" />
      </motion.div>
    </div>
  );
};

// Video URL constants
const DEMO_VIDEO_URL = "https://customer-assets.emergentagent.com/job_minimalist-pitch/artifacts/ramvkjov_Lexy%20Product%20Walkthrough%20New%202030%20%281%29.mp4";
const PREVIEW_VIDEO_URL = "https://customer-assets.emergentagent.com/job_minimalist-pitch/artifacts/88wjk474_preview_video_target.mp4";

const LandingLinear2: React.FC<LandingLinear2Props> = ({ lang = 'nb' }) => {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const t = landingTranslations[lang];
  
  // Video modal state
  const [showVideoModal, setShowVideoModal] = useState(false);
  
  // Floating demo widget state
  const [showDemoWidget, setShowDemoWidget] = useState(false);
  const [widgetDismissed, setWidgetDismissed] = useState(false);
  
  // Show widget after mockup animation completes (mockup: 0.8s delay + 1.1s duration = ~2s)
  useEffect(() => {
    if (!widgetDismissed) {
      const timer = setTimeout(() => {
        setShowDemoWidget(true);
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [widgetDismissed]);
  
  // Autoplay menu animation
  const [activeMenu, setActiveMenu] = useState(0);
  const menuItems = [
    { id: 'overview', label: t.app.overview, icon: 'home' },
    { id: 'inbox', label: t.app.inbox, icon: 'mail' },
    { id: 'ai', label: t.app.aiWorkspace, icon: 'sparkles' },
    { id: 'cases', label: t.app.cases, icon: 'folder' },
    { id: 'time', label: t.app.timeTracking, icon: 'clock' },
  ];
  
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveMenu((prev) => (prev + 1) % menuItems.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div ref={containerRef} className="min-h-screen bg-[#08080a] overflow-hidden">
      
      {/* Living Structure Background */}
      <LivingStructureBackground />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-8 h-14 sm:h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 sm:gap-2.5">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <Zap className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
            </div>
            <span className="text-sm font-medium text-white/80">Lexy</span>
          </Link>
          
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-sm text-white/45 hover:text-white/70 transition-colors">{t.nav.features}</a>
            <a href="#pricing" className="text-sm text-white/45 hover:text-white/70 transition-colors">{t.nav.pricing}</a>
          </div>
          
          <div className="flex items-center gap-3 sm:gap-6">
            <button
              onClick={() => navigate(lang === 'en' ? '/en/lexy/login' : '/lexy/login')}
              className="hidden sm:block text-sm text-white/50 hover:text-white/80 transition-colors"
            >
              {t.nav.login}
            </button>
            <button
              onClick={() => navigate(lang === 'en' ? '/en/lexy/login' : '/lexy/login')}
              className="text-xs sm:text-sm text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-white/10 hover:bg-white/15 border border-white/10 hover:border-white/20 transition-all"
            >
              {t.heroLinear.cta}
            </button>
          </div>
        </div>
      </nav>

      {/* Hero - Left aligned like Linear */}
      <section className="relative pt-24 sm:pt-32 md:pt-36 pb-8 sm:pb-12 md:pb-20">
        
        {/* Subtle ambient glow */}
        <div className="absolute top-[15%] left-[25%] w-[400px] md:w-[700px] h-[300px] md:h-[500px] bg-violet-500/[0.06] blur-[120px] md:blur-[180px] pointer-events-none" />
        
        <div className="max-w-6xl mx-auto px-5 sm:px-6 md:px-8 relative z-10">
          
          {/* Headline - World-class blur to clear */}
          <h1 className="text-[2rem] sm:text-[2.75rem] md:text-[3.5rem] lg:text-[4.5rem] font-light tracking-[-0.03em] leading-[1.1] sm:leading-[1.05] mb-5 sm:mb-7 max-w-5xl">
            <span className="block">
              {t.heroLinear.line1.map((word, i) => (
                <span key={i} className="inline-block overflow-hidden mr-[0.25em]">
                  <motion.span
                    initial={{ 
                      opacity: 0, 
                      y: 10,
                      filter: 'blur(14px)'
                    }}
                    animate={{ 
                      opacity: 1, 
                      y: 0,
                      filter: 'blur(0px)'
                    }}
                    transition={{ 
                      duration: 0.9, 
                      delay: 0.15 + i * 0.1,
                      ease: [0.2, 0.65, 0.3, 0.9]
                    }}
                    className="inline-block text-white"
                  >
                    {word}
                  </motion.span>
                </span>
              ))}
            </span>
            <span className="block">
              {t.heroLinear.line2.map((word, i) => (
                <span key={i} className="inline-block overflow-hidden mr-[0.25em]">
                  <motion.span
                    initial={{ 
                      opacity: 0, 
                      y: 10,
                      filter: 'blur(14px)'
                    }}
                    animate={{ 
                      opacity: 1, 
                      y: 0,
                      filter: 'blur(0px)'
                    }}
                    transition={{ 
                      duration: 0.9, 
                      delay: 0.35 + i * 0.1,
                      ease: [0.2, 0.65, 0.3, 0.9]
                    }}
                    className="inline-block text-white"
                  >
                    {word}
                  </motion.span>
                </span>
              ))}
            </span>
          </h1>
          
          {/* Subheadline - Smooth blur fade */}
          <p className="text-base sm:text-lg md:text-xl text-white/40 max-w-lg mb-6 sm:mb-8 md:mb-10 leading-relaxed font-light tracking-[-0.01em]">
            <span className="block">
              {t.heroLinear.sub1.map((word, i) => (
                <span key={i} className="inline-block overflow-hidden mr-[0.25em]">
                  <motion.span
                    initial={{ 
                      opacity: 0, 
                      filter: 'blur(12px)'
                    }}
                    animate={{ 
                      opacity: 1, 
                      filter: 'blur(0px)'
                    }}
                    transition={{ 
                      duration: 0.8, 
                      delay: 0.6 + i * 0.07,
                      ease: [0.2, 0.65, 0.3, 0.9]
                    }}
                    className="inline-block"
                  >
                    {word}
                  </motion.span>
                </span>
              ))}
            </span>
            <span className="block">
              {t.heroLinear.sub2.map((word, i) => (
                <span key={i} className="inline-block overflow-hidden mr-[0.25em]">
                  <motion.span
                    initial={{ 
                      opacity: 0, 
                      filter: 'blur(12px)'
                    }}
                    animate={{ 
                      opacity: 1, 
                      filter: 'blur(0px)'
                    }}
                    transition={{ 
                      duration: 0.8, 
                      delay: 0.81 + i * 0.07,
                      ease: [0.2, 0.65, 0.3, 0.9]
                    }}
                    className="inline-block"
                  >
                    {word}
                  </motion.span>
                </span>
              ))}
            </span>
          </p>
          
          {/* CTAs - Elegant fade */}
          <motion.div 
            initial={{ opacity: 0, filter: 'blur(10px)' }}
            animate={{ opacity: 1, filter: 'blur(0px)' }}
            transition={{ duration: 0.8, delay: 1, ease: [0.2, 0.65, 0.3, 0.9] }}
            className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 relative z-20"
          >
            <button
              onClick={() => navigate(lang === 'en' ? '/en/lexy/login' : '/lexy/login')}
              className="px-5 sm:px-6 py-2.5 sm:py-3 text-[14px] sm:text-[15px] font-medium text-black bg-white rounded-full hover:bg-white/90 transition-all duration-500 tracking-[-0.01em]"
            >
              {t.heroLinear.cta}
            </button>
            
            <button 
              onClick={() => setShowVideoModal(true)}
              data-testid="watch-demo-btn"
              className="flex items-center gap-2 text-[14px] sm:text-[15px] text-white/50 hover:text-white/80 transition-all duration-500 tracking-[-0.01em] group"
            >
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white/[0.08] group-hover:bg-white/[0.12] flex items-center justify-center transition-all duration-300">
                <Play className="w-3 h-3 sm:w-3.5 sm:h-3.5 ml-0.5" fill="currentColor" />
              </div>
              <span>{t.heroLinear.demo}</span>
            </button>
          </motion.div>
        </div>
        
        {/* Screenshot - Full viewport, Linear-style with rotate + skew - Hidden on mobile */}
        <div className="relative mt-8 sm:mt-10 md:mt-12 hidden md:flex justify-center pl-[20%]" style={{ perspective: '1200px' }}>
          
          <motion.div 
            className="relative w-[100vw] h-[100vh]"
            initial={{ opacity: 0, y: 30, filter: 'blur(16px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            transition={{ duration: 1.1, delay: 0.8, ease: [0.2, 0.65, 0.3, 0.9] }}
          >
            <div
              className="w-full h-full relative"
              style={{ 
                transform: 'rotateX(42deg) rotateY(26deg) rotateZ(12deg) skew(40deg, -40deg)',
                transformStyle: 'preserve-3d',
              }}
            >
            {/* Main screenshot - Minimalist Lexy Inbox */}
            <div 
              className="rounded-2xl overflow-hidden bg-[#0a0a0c] shadow-2xl shadow-black/50 relative"
              style={{
                border: '1px solid rgba(255,255,255,0.08)',
                maskImage: 'linear-gradient(to right, black 0%, black 80%, transparent 100%), linear-gradient(to bottom, black 0%, black 75%, transparent 95%)',
                WebkitMaskImage: 'linear-gradient(to right, black 0%, black 80%, transparent 100%), linear-gradient(to bottom, black 0%, black 75%, transparent 95%)',
                maskComposite: 'intersect',
                WebkitMaskComposite: 'source-in',
              }}
            >
              {/* Minimal browser header - subtle dots and arrows */}
              <div className="absolute top-3 left-3 z-20 flex items-center gap-2.5">
                {/* Traffic lights - more subtle */}
                <div className="flex gap-1">
                  <div className="w-2 h-2 rounded-full bg-white/[0.06]" />
                  <div className="w-2 h-2 rounded-full bg-white/[0.06]" />
                  <div className="w-2 h-2 rounded-full bg-white/[0.06]" />
                </div>
                {/* Navigation arrows - more subtle */}
                <div className="flex gap-0.5 ml-1">
                  <svg className="w-3 h-3 text-white/[0.08]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                  </svg>
                  <svg className="w-3 h-3 text-white/[0.08]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                </div>
              </div>
              
              {/* App content - starts at the top */}
              <div className="flex min-h-[700px]">
                {/* Sidebar - Same as app with animated active state */}
                <div className="w-52 border-r border-white/[0.04] bg-[#0a0a0c] pt-10 pb-3">
                  {/* Logo - smaller with more space above */}
                  <div className="flex items-center gap-2 px-3 mb-5">
                    <div className="w-6 h-6 rounded-md bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                      <Zap className="w-3 h-3 text-white" strokeWidth={2.5} />
                    </div>
                    <span className="text-[13px] font-medium text-white/80 tracking-tight">Lexy</span>
                  </div>
                  
                  {/* ARBEID group */}
                  <div className="px-4 pt-1 pb-2">
                    <span className="text-[9px] font-medium text-white/15 uppercase tracking-[0.12em]">{t.app.work}</span>
                  </div>
                  <div className="px-2 space-y-0.5">
                    <motion.div 
                      className={`flex items-center gap-3 px-2.5 h-9 rounded-[4px] text-[13px] transition-all duration-300 ${activeMenu === 0 ? 'bg-white/[0.08] text-white border-l-2 border-white/50 -ml-0.5 pl-2.5' : 'text-white/45'}`}
                    >
                      <svg className={`w-[17px] h-[17px] ${activeMenu === 0 ? 'text-white/90' : 'text-white/40'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
                      </svg>
                      <span className={activeMenu === 0 ? 'font-medium' : ''}>{t.app.overview}</span>
                    </motion.div>
                    <motion.div 
                      className={`flex items-center gap-3 px-2.5 h-9 rounded-[4px] text-[13px] transition-all duration-300 ${activeMenu === 1 ? 'bg-white/[0.08] text-white border-l-2 border-white/50 -ml-0.5 pl-2.5' : 'text-white/45'}`}
                    >
                      <Mail className={`w-[17px] h-[17px] ${activeMenu === 1 ? 'text-white/90' : 'text-white/40'}`} strokeWidth={1.5} />
                      <span className={activeMenu === 1 ? 'font-medium' : ''}>{t.app.inbox}</span>
                      {activeMenu === 1 && <span className="ml-auto text-[10px] bg-violet-500/80 text-white px-2 py-0.5 rounded-full">8</span>}
                    </motion.div>
                    <div className="flex items-center gap-3 px-2.5 h-9 text-white/45 text-[13px]">
                      <svg className="w-[17px] h-[17px] text-white/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 12h.007v.008H3.75V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm-.375 5.25h.007v.008H3.75v-.008zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                      </svg>
                      <span>{t.app.workQueue}</span>
                    </div>
                    <motion.div 
                      className={`flex items-center gap-3 px-2.5 h-9 rounded-[4px] text-[13px] transition-all duration-300 ${activeMenu === 2 ? 'bg-white/[0.08] text-white border-l-2 border-white/50 -ml-0.5 pl-2.5' : 'text-white/45'}`}
                    >
                      <Sparkles className={`w-[17px] h-[17px] ${activeMenu === 2 ? 'text-white/90' : 'text-white/40'}`} strokeWidth={1.5} />
                      <span className={activeMenu === 2 ? 'font-medium' : ''}>{t.app.aiWorkspace}</span>
                    </motion.div>
                  </div>
                  
                  {/* SAKER group */}
                  <div className="px-4 pt-6 pb-2">
                    <span className="text-[10px] font-medium text-white/15 uppercase tracking-[0.12em]">{t.app.cases}</span>
                  </div>
                  <div className="px-2 space-y-0.5">
                    <motion.div 
                      className={`flex items-center gap-3 px-2.5 h-9 rounded-[4px] text-[13px] transition-all duration-300 ${activeMenu === 3 ? 'bg-white/[0.08] text-white border-l-2 border-white/50 -ml-0.5 pl-2.5' : 'text-white/45'}`}
                    >
                      <svg className={`w-[17px] h-[17px] ${activeMenu === 3 ? 'text-white/90' : 'text-white/40'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                      </svg>
                      <span className={activeMenu === 3 ? 'font-medium' : ''}>{t.app.cases}</span>
                      {activeMenu === 3 && <span className="ml-auto text-[10px] bg-white/10 text-white/60 px-2 py-0.5 rounded-full">12</span>}
                    </motion.div>
                    <div className="flex items-center gap-3 px-2.5 h-9 text-white/45 text-[13px]">
                      <svg className="w-[17px] h-[17px] text-white/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                      </svg>
                      <span>{t.app.clients}</span>
                    </div>
                    <div className="flex items-center gap-3 px-2.5 h-9 text-white/45 text-[13px]">
                      <svg className="w-[17px] h-[17px] text-white/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
                      </svg>
                      <span>{t.app.documents}</span>
                    </div>
                  </div>
                  
                  {/* ADMIN group */}
                  <div className="px-4 pt-6 pb-2">
                    <span className="text-[10px] font-medium text-white/15 uppercase tracking-[0.12em]">{t.app.admin}</span>
                  </div>
                  <div className="px-2 space-y-0.5">
                    <motion.div 
                      className={`flex items-center gap-3 px-2.5 h-9 rounded-[4px] text-[13px] transition-all duration-300 ${activeMenu === 4 ? 'bg-white/[0.08] text-white border-l-2 border-white/50 -ml-0.5 pl-2.5' : 'text-white/45'}`}
                    >
                      <Clock className={`w-[17px] h-[17px] ${activeMenu === 4 ? 'text-white/90' : 'text-white/40'}`} strokeWidth={1.5} />
                      <span className={activeMenu === 4 ? 'font-medium' : ''}>{t.app.timeTracking}</span>
                    </motion.div>
                    <div className="flex items-center gap-3 px-2.5 h-9 text-white/45 text-[13px]">
                      <svg className="w-[17px] h-[17px] text-white/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>{t.app.invoicing}</span>
                    </div>
                  </div>
                </div>
                
                {/* Main content - Animated based on activeMenu */}
                <AnimatePresence mode="wait">
                  <motion.div 
                    key={activeMenu}
                    initial={{ opacity: 0, filter: 'blur(8px)' }}
                    animate={{ opacity: 1, filter: 'blur(0px)' }}
                    exit={{ opacity: 0, filter: 'blur(4px)' }}
                    transition={{ duration: 0.5, ease: [0.2, 0.65, 0.3, 0.9] }}
                    className="flex-1 flex"
                  >
                    {activeMenu === 0 && (
                      /* Overview - ChatGPT style welcome - ULTRA WORLD CLASS */
                      <div className="flex-1 flex flex-col items-center justify-center p-8 bg-[#08080a] relative overflow-hidden">
                        {/* Multiple layered ambient gradients */}
                        <div className="absolute inset-0 pointer-events-none">
                          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-violet-500/[0.04] blur-[120px]" />
                          <div className="absolute top-1/3 left-1/3 w-[300px] h-[300px] rounded-full bg-indigo-500/[0.03] blur-[100px]" />
                        </div>
                        
                        <div className="text-center max-w-lg relative z-10">
                          {/* Minimalist logo with refined glow */}
                          <motion.div 
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.8, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
                            className="relative w-14 h-14 mx-auto mb-5"
                          >
                            <div className="absolute inset-0 rounded-2xl bg-violet-500/25 blur-2xl scale-150" />
                            <div className="relative w-full h-full rounded-2xl bg-gradient-to-br from-violet-500 via-violet-600 to-purple-700 flex items-center justify-center shadow-xl">
                              <Sparkles className="w-6 h-6 text-white" strokeWidth={1.8} />
                            </div>
                          </motion.div>
                          
                          {/* Elegant welcome text */}
                          <motion.h2 
                            initial={{ opacity: 0, y: 12, filter: 'blur(8px)' }}
                            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                            transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
                            className="text-2xl font-normal text-white mb-2 tracking-[-0.02em]"
                          >
                            {t.app.goodMorning}, Maria
                          </motion.h2>
                          <motion.p 
                            initial={{ opacity: 0, filter: 'blur(6px)' }}
                            animate={{ opacity: 1, filter: 'blur(0px)' }}
                            transition={{ duration: 0.6, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
                            className="text-white/35 text-sm mb-6"
                          >
                            {t.app.howCanIHelp}
                          </motion.p>
                          
                          {/* Premium quick action cards - 2x2 grid */}
                          <motion.div 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.5, delay: 0.3 }}
                            className="grid grid-cols-2 gap-2 mb-5"
                          >
                            {[
                              { icon: FileText, title: t.app.analyzeContract, desc: lang === 'en' ? 'AI document analysis' : 'AI dokumentanalyse', gradient: 'from-blue-500/10 to-blue-600/5' },
                              { icon: PenTool, title: t.app.writeMotion, desc: lang === 'en' ? 'Generate draft' : 'Generer utkast', gradient: 'from-violet-500/10 to-violet-600/5' },
                              { icon: Shield, title: t.app.searchCaseLaw, desc: lang === 'en' ? 'Find relevant cases' : 'Finn relevante dommer', gradient: 'from-emerald-500/10 to-emerald-600/5' },
                              { icon: Mail, title: t.app.draftResponse, desc: lang === 'en' ? 'AI-assisted reply' : 'AI-assistert svar', gradient: 'from-amber-500/10 to-amber-600/5' },
                            ].map((item, i) => (
                              <motion.div 
                                key={i}
                                initial={{ opacity: 0, y: 10, scale: 0.97 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                transition={{ duration: 0.5, delay: 0.35 + i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                                className={`p-3 rounded-xl bg-gradient-to-br ${item.gradient} border border-white/[0.04] hover:border-white/[0.08] transition-all duration-300 cursor-pointer text-left group`}
                              >
                                <item.icon className="w-4 h-4 text-white/40 mb-1.5 group-hover:text-white/60 transition-colors" strokeWidth={1.5} />
                                <span className="text-[12px] text-white/70 font-medium block mb-0.5 group-hover:text-white/90 transition-colors">{item.title}</span>
                                <span className="text-[9px] text-white/25">{item.desc}</span>
                              </motion.div>
                            ))}
                          </motion.div>
                          
                          {/* Premium chat input */}
                          <motion.div 
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.55, ease: [0.16, 1, 0.3, 1] }}
                            className="relative mb-4"
                          >
                            <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/[0.03] border border-white/[0.06] shadow-lg shadow-black/10">
                              <Brain className="w-4 h-4 text-violet-400/40" strokeWidth={1.5} />
                              <span className="flex-1 text-[12px] text-white/25 text-left">{t.app.askAnything}</span>
                              <div className="w-6 h-6 rounded-lg bg-violet-500/80 flex items-center justify-center">
                                <ArrowRight className="w-3 h-3 text-white" />
                              </div>
                            </div>
                          </motion.div>
                          
                          {/* Recent activity hint */}
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.5, delay: 0.7 }}
                            className="flex items-center justify-center gap-4 text-[10px] text-white/20"
                          >
                            <span>{lang === 'en' ? '3 unread emails' : '3 uleste e-poster'}</span>
                            <span className="w-1 h-1 rounded-full bg-white/10" />
                            <span>{lang === 'en' ? '2 deadlines today' : '2 frister i dag'}</span>
                            <span className="w-1 h-1 rounded-full bg-white/10" />
                            <span>5 aktive saker</span>
                          </motion.div>
                        </div>
                      </div>
                    )}
                    
                    {activeMenu === 1 && (
                      /* Inbox content with AI Analysis - ULTRA WORLD CLASS */
                      <>
                        <div className="flex-1 p-5 bg-[#08080a]">
                          <motion.div 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.4 }}
                            className="flex items-center justify-between mb-4"
                          >
                            <div className="flex items-center gap-2.5">
                              <h2 className="text-[13px] font-medium text-white/70">{t.app.inbox}</h2>
                              <span className="px-1.5 py-0.5 rounded-md bg-violet-500/15 text-violet-400 text-[9px] font-medium">{lang === 'en' ? '8 unread' : '8 uleste'}</span>
                            </div>
                          </motion.div>
                          
                          {/* Premium email list - more items for height */}
                          <div className="space-y-0.5">
                            {(lang === 'en' ? [
                              { initials: 'BL', name: 'Berg Law LLP', subject: 'Due diligence - Merger documents', time: '10:42', gradient: 'from-blue-400 to-blue-600', active: true, tags: ['AI analyzed', 'Urgent'], hasAttachment: true },
                              { initials: 'DC', name: 'District Court', subject: 'Notice of hearing', time: '09:15', gradient: 'from-slate-500 to-slate-600', unread: true },
                              { initials: 'HE', name: 'Hansen Estate Ltd', subject: 'Lease agreement inquiry', time: 'Yesterday', gradient: 'from-emerald-400 to-emerald-600' },
                              { initials: 'JD', name: 'Jane Doe', subject: 'Re: Estate case - Documentation', time: 'Yesterday', gradient: 'from-rose-400 to-rose-500' },
                              { initials: 'NI', name: 'Nordic Invest', subject: 'Merger process follow-up', time: '2 days', gradient: 'from-indigo-400 to-indigo-600' },
                              { initials: 'TR', name: 'Tax Revenue', subject: 'Assessment decision', time: '3 days', gradient: 'from-amber-500 to-amber-600' },
                            ] : [
                              { initials: 'AB', name: 'Advokatfirma Berg AS', subject: 'Due diligence - Fusjonsdokumenter', time: '10:42', gradient: 'from-blue-400 to-blue-600', active: true, tags: ['AI analysert', 'Haster'], hasAttachment: true },
                              { initials: 'OT', name: 'Oslo Tingrett', subject: 'Innkalling til hovedforhandling', time: '09:15', gradient: 'from-slate-500 to-slate-600', unread: true },
                              { initials: 'HE', name: 'Hansen Eiendom AS', subject: 'Spørsmål om leiekontrakt', time: 'I går', gradient: 'from-emerald-400 to-emerald-600' },
                              { initials: 'KN', name: 'Kari Nordmann', subject: 'Re: Arvesak - Dokumentasjon', time: 'I går', gradient: 'from-rose-400 to-rose-500' },
                              { initials: 'NI', name: 'Nordvik Invest', subject: 'Oppfølging fusjonsprosess', time: '2 dager', gradient: 'from-indigo-400 to-indigo-600' },
                              { initials: 'SK', name: 'Skatt Øst', subject: 'Vedtak om ligningsendring', time: '3 dager', gradient: 'from-amber-500 to-amber-600' },
                            ]).map((email, i) => (
                              <motion.div 
                                key={i}
                                initial={{ opacity: 0, x: -15 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.4, delay: 0.05 + i * 0.03, ease: [0.16, 1, 0.3, 1] }}
                                className={`p-2.5 rounded-lg transition-all cursor-pointer ${email.active ? 'bg-white/[0.04] border border-violet-500/20' : 'hover:bg-white/[0.02]'}`}
                              >
                                <div className="flex items-start gap-2.5">
                                  <div className="relative flex-shrink-0">
                                    <div className={`w-7 h-7 rounded-full bg-gradient-to-br ${email.gradient} flex items-center justify-center text-white text-[9px] font-semibold`}>{email.initials}</div>
                                    {email.unread && <div className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-violet-500 border border-[#08080a]" />}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between mb-0.5">
                                      <p className={`text-[11px] font-medium truncate ${email.active || email.unread ? 'text-white/90' : 'text-white/60'}`}>{email.name}</p>
                                      <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                                        {email.hasAttachment && <svg className="w-2.5 h-2.5 text-white/25" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13" /></svg>}
                                        <span className="text-[9px] text-white/25">{email.time}</span>
                                      </div>
                                    </div>
                                    <p className={`text-[10px] truncate ${email.active ? 'text-white/50' : 'text-white/35'}`}>{email.subject}</p>
                                    {email.tags && (
                                      <div className="flex items-center gap-1.5 mt-1">
                                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-violet-500/10 text-violet-400 text-[8px] font-medium"><Sparkles className="w-2 h-2" />{email.tags[0]}</span>
                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 text-[8px] font-medium">{email.tags[1]}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </motion.div>
                            ))}
                          </div>
                        </div>
                        
                        {/* AI Analysis Panel - ULTRA WORLD CLASS */}
                        <motion.div 
                          initial={{ opacity: 0, x: 15 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.5, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
                          className="w-64 border-l border-white/[0.03] bg-[#09090b] p-4"
                        >
                          <div className="flex items-center gap-2 mb-4">
                            <motion.div 
                              initial={{ scale: 0.8 }}
                              animate={{ scale: 1 }}
                              transition={{ duration: 0.4, delay: 0.2 }}
                              className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/15"
                            >
                              <Sparkles className="w-3 h-3 text-white" strokeWidth={2} />
                            </motion.div>
                            <div>
                              <span className="text-[11px] font-medium text-white/80 block">{lang === 'en' ? 'AI Analysis' : 'AI Analyse'}</span>
                              <span className="text-[8px] text-white/25">{lang === 'en' ? 'Analyzed now' : 'Analysert nå'}</span>
                            </div>
                          </div>
                          
                          <div className="space-y-2">
                            {/* Summary */}
                            <motion.div 
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.4, delay: 0.25 }}
                              className="p-2.5 rounded-lg bg-gradient-to-br from-violet-500/[0.08] to-purple-500/[0.03] border border-violet-500/15"
                            >
                              <div className="flex items-center gap-1.5 mb-1">
                                <div className="w-1 h-1 rounded-full bg-violet-400" />
                                <p className="text-[8px] text-violet-400 font-semibold uppercase tracking-wider">{lang === 'en' ? 'Summary' : 'Sammendrag'}</p>
                              </div>
                              <p className="text-[10px] text-white/60 leading-relaxed">
                                {lang === 'en' 
                                  ? <>Due diligence for merger between Company A and B. <span className="text-amber-400/90">Deadline: Jan 15, 2025.</span></>
                                  : <>Due diligence for fusjon mellom Selskap A og B. <span className="text-amber-400/90">Frist: 15. jan 2025.</span></>
                                }
                              </p>
                            </motion.div>
                            
                            {/* Key points - compact */}
                            <motion.div 
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.4, delay: 0.35 }}
                              className="p-2.5 rounded-lg bg-white/[0.02]"
                            >
                              <p className="text-[8px] text-white/40 font-semibold uppercase tracking-wider mb-1.5">{lang === 'en' ? 'Identified' : 'Identifisert'}</p>
                              <div className="space-y-1">
                                {(lang === 'en' ? [
                                  { icon: '📅', text: 'Deadline: January 15', color: 'text-amber-400/80' },
                                  { icon: '📋', text: 'Type: Due diligence', color: 'text-white/50' },
                                  { icon: '🏢', text: 'Parties: Berg, Hansen', color: 'text-white/50' },
                                  { icon: '⚠️', text: 'Priority: High', color: 'text-rose-400/80' },
                                ] : [
                                  { icon: '📅', text: 'Frist: 15. januar', color: 'text-amber-400/80' },
                                  { icon: '📋', text: 'Type: Due diligence', color: 'text-white/50' },
                                  { icon: '🏢', text: 'Parter: Berg, Hansen', color: 'text-white/50' },
                                  { icon: '⚠️', text: 'Prioritet: Høy', color: 'text-rose-400/80' },
                                ]).map((point, i) => (
                                  <motion.div 
                                    key={i}
                                    initial={{ opacity: 0, x: -8 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.3, delay: 0.4 + i * 0.03 }}
                                    className="flex items-center gap-1.5"
                                  >
                                    <span className="text-[9px]">{point.icon}</span>
                                    <span className={`text-[9px] ${point.color}`}>{point.text}</span>
                                  </motion.div>
                                ))}
                              </div>
                            </motion.div>
                            
                            {/* Actions */}
                            <motion.div 
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.4, delay: 0.5 }}
                              className="space-y-1"
                            >
                              <p className="text-[8px] text-white/40 font-semibold uppercase tracking-wider mb-1">Handlinger</p>
                              {[
                                { icon: <FolderOpen className="w-2.5 h-2.5" />, text: 'Opprett sak', primary: true },
                                { icon: <Clock className="w-2.5 h-2.5" />, text: 'Legg i kalender' },
                                { icon: <ArrowRight className="w-2.5 h-2.5" />, text: 'Generer svar' },
                              ].map((action, i) => (
                                <motion.button 
                                  key={i}
                                  initial={{ opacity: 0, scale: 0.95 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  transition={{ duration: 0.3, delay: 0.55 + i * 0.03 }}
                                  className={`w-full text-left px-2 py-1.5 rounded-lg text-[9px] flex items-center gap-1.5 transition-all ${action.primary ? 'bg-violet-500/15 text-violet-300' : 'bg-white/[0.02] text-white/40'}`}
                                >
                                  {action.icon}
                                  {action.text}
                                </motion.button>
                              ))}
                            </motion.div>
                          </div>
                        </motion.div>
                      </>
                    )}
                    
                    {activeMenu === 2 && (
                      /* AI Workspace content - ULTRA WORLD CLASS */
                      <div className="flex-1 p-5 bg-[#08080a] relative overflow-hidden">
                        {/* Subtle ambient glow */}
                        <div className="absolute top-0 right-0 w-64 h-64 bg-violet-500/[0.03] blur-[100px] pointer-events-none" />
                        
                        <motion.div 
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="flex items-center justify-between mb-4 relative z-10"
                        >
                          <h2 className="text-[13px] font-medium text-white/70">AI Workspace</h2>
                          <motion.div 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.15 }}
                            className="flex items-center gap-1.5"
                          >
                            <motion.div 
                              className="w-1.5 h-1.5 rounded-full bg-emerald-400"
                              animate={{ scale: [1, 1.2, 1], opacity: [1, 0.5, 1] }}
                              transition={{ duration: 2, repeat: Infinity }}
                            />
                            <span className="text-[10px] text-emerald-400/70">Aktiv</span>
                          </motion.div>
                        </motion.div>
                        
                        <div className="space-y-2.5 relative z-10">
                          {/* Active analysis - Premium card */}
                          <motion.div 
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
                            className="p-3.5 rounded-xl bg-gradient-to-br from-violet-500/[0.08] to-transparent border border-violet-500/15"
                          >
                            <div className="flex items-center justify-between mb-2.5">
                              <div className="flex items-center gap-2">
                                <motion.div 
                                  initial={{ scale: 0.8 }}
                                  animate={{ scale: 1 }}
                                  transition={{ duration: 0.3, delay: 0.1 }}
                                  className="w-7 h-7 rounded-lg bg-violet-500/15 flex items-center justify-center"
                                >
                                  <Sparkles className="w-3 h-3 text-violet-400" strokeWidth={1.8} />
                                </motion.div>
                                <div>
                                  <span className="text-[11px] font-medium text-white/80 block">Dokumentanalyse</span>
                                  <span className="text-[9px] text-white/30">Fusjonsavtale.pdf</span>
                                </div>
                              </div>
                              <span className="text-[8px] text-violet-400/80 bg-violet-500/10 px-1.5 py-0.5 rounded-full">
                                Analyserer...
                              </span>
                            </div>
                            
                            {/* Progress items */}
                            <div className="space-y-1 mb-2.5">
                              {[
                                { text: 'Identifisert 12 nøkkelklausuler', done: true },
                                { text: 'Flagget 3 risikoområder', done: true },
                                { text: 'Genererer sammendrag...', done: false },
                              ].map((item, i) => (
                                <motion.div 
                                  key={i}
                                  initial={{ opacity: 0, x: -10 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ duration: 0.3, delay: 0.15 + i * 0.05 }}
                                  className="flex items-center gap-1.5 text-[9px] text-white/40"
                                >
                                  {item.done ? (
                                    <div className="w-3 h-3 rounded-full bg-emerald-500/15 flex items-center justify-center">
                                      <Check className="w-1.5 h-1.5 text-emerald-400" />
                                    </div>
                                  ) : (
                                    <div className="w-3 h-3 rounded-full border border-white/10 flex items-center justify-center">
                                      <motion.div 
                                        className="w-1 h-1 rounded-full bg-violet-400"
                                        animate={{ opacity: [0.3, 1, 0.3] }}
                                        transition={{ duration: 1.2, repeat: Infinity }}
                                      />
                                    </div>
                                  )}
                                  <span className={item.done ? 'text-white/50' : 'text-white/30'}>{item.text}</span>
                                </motion.div>
                              ))}
                            </div>
                            
                            {/* Progress bar */}
                            <div className="h-0.5 bg-white/5 rounded-full overflow-hidden">
                              <motion.div 
                                className="h-full bg-gradient-to-r from-violet-500 to-purple-500 rounded-full"
                                initial={{ width: '0%' }}
                                animate={{ width: '72%' }}
                                transition={{ duration: 1.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                              />
                            </div>
                          </motion.div>
                          
                          {/* Recent conversations */}
                          <motion.div 
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: 0.25 }}
                            className="p-3 rounded-xl bg-white/[0.02]"
                          >
                            <p className="text-[8px] text-white/30 uppercase tracking-wider mb-2">Siste samtaler</p>
                            <div className="space-y-1.5">
                              {[
                                { title: 'Kontraktsanalyse Berg AS', time: '2 min siden' },
                                { title: 'Rettspraksissøk - Husleieloven', time: '1 time siden' },
                                { title: 'Prosesskriv utkast', time: '3 timer siden' },
                              ].map((conv, i) => (
                                <motion.div 
                                  key={i}
                                  initial={{ opacity: 0, x: -8 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: 0.3 + i * 0.04 }}
                                  className="flex items-center justify-between p-1.5 rounded-lg hover:bg-white/[0.02] transition-colors cursor-pointer"
                                >
                                  <span className="text-[10px] text-white/50">{conv.title}</span>
                                  <span className="text-[8px] text-white/20">{conv.time}</span>
                                </motion.div>
                              ))}
                            </div>
                          </motion.div>
                          
                          {/* Quick actions */}
                          <motion.div 
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: 0.35 }}
                            className="flex gap-2"
                          >
                            {['Skriv kontrakt', 'Analyser', 'Søk rettspraksis'].map((text, i) => (
                              <motion.span 
                                key={text}
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.4 + i * 0.04 }}
                                className="px-2 py-1 rounded-lg bg-white/[0.03] text-white/35 text-[9px] hover:bg-white/[0.05] transition-colors cursor-pointer"
                              >
                                {text}
                              </motion.span>
                            ))}
                          </motion.div>
                          
                          {/* Chat input - minimalist */}
                          <motion.div 
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: 0.45 }}
                            className="flex items-center gap-2 p-2.5 rounded-xl bg-white/[0.02] border border-white/[0.04]"
                          >
                            <Brain className="w-3.5 h-3.5 text-violet-400/40" strokeWidth={1.5} />
                            <span className="flex-1 text-[10px] text-white/25">Spør AI om hva som helst...</span>
                            <div className="w-5 h-5 rounded-md bg-violet-500/60 flex items-center justify-center">
                              <ArrowRight className="w-2.5 h-2.5 text-white" />
                            </div>
                          </motion.div>
                        </div>
                      </div>
                    )}
                    
                    {activeMenu === 3 && (
                      /* Cases content - ULTRA WORLD CLASS */
                      <div className="flex-1 p-5 bg-[#08080a]">
                        <motion.div 
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="flex items-center justify-between mb-4"
                        >
                          <h2 className="text-[13px] font-medium text-white/70">Aktive saker</h2>
                          <motion.button 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.1 }}
                            className="text-[10px] text-violet-400/70 hover:text-violet-400 transition-colors"
                          >
                            + Ny sak
                          </motion.button>
                        </motion.div>
                        
                        <div className="space-y-1.5">
                          {[
                            { name: 'Berg AS vs. Hansen', type: 'Kontraktstvist', id: 'SAK-2024-001', status: 'Haster', statusColor: 'bg-amber-500/10 text-amber-400', docs: 8, time: '24t', deadline: '2 dager', progress: 65, team: ['MH', 'KN'], active: true },
                            { name: 'Eiendom Holding AS', type: 'Due diligence', id: 'SAK-2024-002', status: 'Aktiv', statusColor: 'bg-emerald-500/10 text-emerald-400', docs: 12, time: '48t', progress: 40, team: ['JH'] },
                            { name: 'Nordvik Invest', type: 'Selskapsfusjon', id: 'SAK-2024-003', status: 'Venter', statusColor: 'bg-blue-500/10 text-blue-400', docs: 3, time: '8t', progress: 15 },
                            { name: 'Solberg Shipping', type: 'Arbeidsrett', id: 'SAK-2024-004', status: 'Ny', statusColor: 'bg-violet-500/10 text-violet-400', docs: 2, time: '2t', progress: 5 },
                            { name: 'Tech Solutions AS', type: 'IP-rettigheter', id: 'SAK-2024-005', status: 'Aktiv', statusColor: 'bg-emerald-500/10 text-emerald-400', docs: 6, time: '16t', progress: 30 },
                          ].map((caseItem, i) => (
                            <motion.div 
                              key={i}
                              initial={{ opacity: 0, x: -15 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ duration: 0.4, delay: 0.05 + i * 0.04, ease: [0.16, 1, 0.3, 1] }}
                              className={`p-3 rounded-xl transition-all cursor-pointer ${caseItem.active ? 'bg-white/[0.03] border border-violet-500/15' : 'bg-white/[0.015] hover:bg-white/[0.025]'}`}
                            >
                              <div className="flex items-start justify-between mb-1.5">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-0.5">
                                    <span className={`text-[11px] font-medium truncate ${caseItem.active ? 'text-white/85' : 'text-white/60'}`}>{caseItem.name}</span>
                                    <span className={`text-[7px] px-1.5 py-0.5 rounded ${caseItem.statusColor} font-medium flex-shrink-0`}>
                                      {caseItem.status}
                                    </span>
                                  </div>
                                  <span className="text-[9px] text-white/25">{caseItem.type} • {caseItem.id}</span>
                                </div>
                              </div>
                              
                              {/* Progress bar */}
                              <div className="h-0.5 bg-white/5 rounded-full overflow-hidden mb-1.5">
                                <motion.div 
                                  className="h-full bg-gradient-to-r from-violet-500/60 to-purple-500/60 rounded-full"
                                  initial={{ width: '0%' }}
                                  animate={{ width: `${caseItem.progress}%` }}
                                  transition={{ duration: 1, delay: 0.15 + i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                                />
                              </div>
                              
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2.5 text-[8px] text-white/30">
                                  <span className="flex items-center gap-1">
                                    <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>
                                    {caseItem.docs}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-2.5 h-2.5" strokeWidth={1.5} />
                                    {caseItem.time}
                                  </span>
                                  {caseItem.deadline && <span className="text-amber-400/70">Frist: {caseItem.deadline}</span>}
                                </div>
                                {caseItem.team && (
                                  <div className="flex -space-x-1">
                                    {caseItem.team.map((initials, j) => (
                                      <motion.div 
                                        key={j}
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        transition={{ delay: 0.2 + j * 0.03 }}
                                        className={`w-4 h-4 rounded-full bg-gradient-to-br ${j === 0 ? 'from-blue-400 to-blue-600' : 'from-violet-400 to-violet-600'} border border-[#08080a] flex items-center justify-center text-[6px] text-white font-semibold`}
                                      >
                                        {initials}
                                      </motion.div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {activeMenu === 4 && (
                      /* Time tracking content - ULTRA WORLD CLASS */
                      <div className="flex-1 p-5 bg-[#08080a]">
                        <motion.div 
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="flex items-center justify-between mb-4"
                        >
                          <h2 className="text-[13px] font-medium text-white/70">Timeføring</h2>
                          <motion.div 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.1 }}
                            className="text-[10px] text-white/30"
                          >
                            Uke: <span className="text-white/50 font-medium">32t 15m</span>
                          </motion.div>
                        </motion.div>
                        
                        <div className="space-y-2">
                          {/* Active timer - Premium */}
                          <motion.div 
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
                            className="p-3.5 rounded-xl bg-gradient-to-br from-emerald-500/10 to-emerald-500/[0.02] border border-emerald-500/15"
                          >
                            <div className="flex items-center justify-between mb-2.5">
                              <div className="flex items-center gap-2">
                                <motion.div 
                                  className="w-2 h-2 rounded-full bg-emerald-400"
                                  animate={{ scale: [1, 1.15, 1], opacity: [1, 0.6, 1] }}
                                  transition={{ duration: 1.8, repeat: Infinity }}
                                />
                                <span className="text-[10px] font-medium text-white/70">Timer aktiv</span>
                              </div>
                              <motion.span 
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.15 }}
                                className="text-lg font-mono text-emerald-400 tabular-nums tracking-tight"
                              >
                                01:24:33
                              </motion.span>
                            </div>
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-[9px] text-white/50 mb-0.5">Berg AS - Dokumentgjennomgang</p>
                                <p className="text-[8px] text-white/25">SAK-2024-001</p>
                              </div>
                              <motion.button 
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ delay: 0.2 }}
                                className="px-2 py-1 rounded-lg bg-white/5 text-white/50 text-[9px] font-medium hover:bg-white/10 transition-colors"
                              >
                                Stopp
                              </motion.button>
                            </div>
                          </motion.div>
                          
                          {/* Today's progress - Clean */}
                          <motion.div 
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: 0.15 }}
                            className="p-3 rounded-xl bg-white/[0.02]"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-[10px] text-white/50">I dag</span>
                              <span className="text-[12px] font-mono text-white/60 tabular-nums">6t 45m</span>
                            </div>
                            <div className="h-1 bg-white/5 rounded-full overflow-hidden mb-1">
                              <motion.div 
                                className="h-full bg-gradient-to-r from-violet-500 to-purple-500 rounded-full"
                                initial={{ width: '0%' }}
                                animate={{ width: '85%' }}
                                transition={{ duration: 1.2, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                              />
                            </div>
                            <div className="flex justify-between text-[7px] text-white/20">
                              <span>0t</span>
                              <span>Mål: 8t</span>
                            </div>
                          </motion.div>
                          
                          {/* Stats - Minimalist grid */}
                          <div className="grid grid-cols-2 gap-1.5">
                            {[
                              { label: 'Fakturerbar', value: '28t 30m', percentage: '+12%', positive: true },
                              { label: 'Ikke-fakt.', value: '3t 45m', percentage: '11%', positive: false },
                            ].map((stat, i) => (
                              <motion.div 
                                key={i}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, delay: 0.25 + i * 0.05 }}
                                className="p-2.5 rounded-lg bg-white/[0.015]"
                              >
                                <p className="text-[8px] text-white/30 mb-0.5">{stat.label}</p>
                                <div className="flex items-end justify-between">
                                  <p className={`text-[12px] font-mono tabular-nums ${i === 0 ? 'text-emerald-400/70' : 'text-white/35'}`}>{stat.value}</p>
                                  <span className={`text-[7px] ${stat.positive ? 'text-emerald-400/50' : 'text-white/20'}`}>{stat.percentage}</span>
                                </div>
                              </motion.div>
                            ))}
                          </div>
                          
                          {/* Recent entries */}
                          <motion.div 
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: 0.35 }}
                            className="p-2.5 rounded-lg bg-white/[0.015]"
                          >
                            <p className="text-[8px] text-white/25 uppercase tracking-wider mb-1.5">Siste føringer</p>
                            <div className="space-y-1">
                              {[
                                { task: 'Kontraktsgjennomgang', time: '2t 15m', case: 'Berg AS' },
                                { task: 'Møte med klient', time: '1t 30m', case: 'Hansen' },
                                { task: 'Prosesskriv utkast', time: '3t 00m', case: 'Nordvik' },
                                { task: 'Dokumentanalyse', time: '45m', case: 'Eiendom' },
                              ].map((entry, i) => (
                                <motion.div 
                                  key={i}
                                  initial={{ opacity: 0, x: -8 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: 0.4 + i * 0.03 }}
                                  className="flex items-center justify-between py-0.5"
                                >
                                  <div>
                                    <span className="text-[9px] text-white/45 block">{entry.task}</span>
                                    <span className="text-[7px] text-white/20">{entry.case}</span>
                                  </div>
                                  <span className="text-[9px] font-mono text-white/35 tabular-nums">{entry.time}</span>
                                </motion.div>
                              ))}
                            </div>
                          </motion.div>
                        </div>
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Video Section - World Class Minimalist */}
      <section className="relative py-12 sm:py-16 md:py-20 overflow-hidden">
        {/* Subtle ambient glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] md:w-[800px] h-[300px] md:h-[500px] bg-violet-500/[0.04] blur-[100px] md:blur-[150px] pointer-events-none" />
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 relative z-10">
          {/* Section header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-8 sm:mb-10 md:mb-12"
          >
            <p className="text-white/30 text-xs sm:text-sm tracking-[0.15em] sm:tracking-[0.2em] uppercase mb-2 sm:mb-3">
              {lang === 'en' ? 'Product Demo' : 'Se Lexy i aksjon'}
            </p>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-light text-white tracking-[-0.02em]">
              {lang === 'en' ? 'Experience the future of legal work' : 'Opplev fremtidens juridiske arbeid'}
            </h2>
          </motion.div>
          
          {/* Video container - Elegant frame */}
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.98 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="relative group"
          >
            {/* Glow effect on hover */}
            <div className="absolute -inset-1 bg-gradient-to-r from-violet-500/20 via-purple-500/20 to-violet-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
            
            {/* Video wrapper */}
            <div 
              className="relative aspect-video rounded-lg sm:rounded-xl md:rounded-2xl overflow-hidden bg-[#0c0c0e] border border-white/[0.08] shadow-2xl shadow-black/50"
              data-testid="demo-video-section"
            >
              <video
                className="w-full h-full object-cover"
                controls
                playsInline
                preload="metadata"
                poster=""
              >
                <source src={DEMO_VIDEO_URL} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              
              {/* Custom play button overlay - shows before video plays */}
              <div 
                className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 hover:opacity-100 transition-opacity duration-300 cursor-pointer"
                onClick={(e) => {
                  const video = e.currentTarget.previousElementSibling as HTMLVideoElement;
                  if (video) {
                    video.play();
                    e.currentTarget.style.display = 'none';
                  }
                }}
              >
                <motion.div 
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-2xl"
                >
                  <Play className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-[#08080a] ml-0.5 sm:ml-1" fill="currentColor" />
                </motion.div>
              </div>
            </div>
            
            {/* Subtle reflection effect */}
            <div className="absolute -bottom-px left-0 right-0 h-10 sm:h-16 md:h-20 bg-gradient-to-t from-[#08080a] via-transparent to-transparent pointer-events-none" />
          </motion.div>
        </div>
      </section>

      {/* Linear-style Section - WORLD CLASS */}
      {/* Features Section - ULTRA MINIMALIST */}
      <section className="relative py-16 sm:py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          
          {/* Section header - Clean and simple */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="text-center mb-12 sm:mb-16 md:mb-24"
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-medium text-white tracking-[-0.03em] leading-[1.15]">
              {lang === 'en' ? 'Built different' : 'Bygget annerledes'}
            </h2>
          </motion.div>
          
          {/* Feature list - Elegant simplicity */}
          <div className="space-y-12 sm:space-y-16 md:space-y-20">
            
            {/* Feature 1 - AI Analysis */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="group"
            >
              <div className="flex flex-col md:flex-row md:items-start gap-4 sm:gap-6 md:gap-16">
                {/* Left - Number & Line */}
                <div className="flex items-center gap-4 sm:gap-6 md:w-48 flex-shrink-0">
                  <span className="text-white/10 text-4xl sm:text-5xl md:text-6xl font-light">01</span>
                  <div className="hidden md:block flex-1 h-px bg-gradient-to-r from-white/10 to-transparent" />
                </div>
                
                {/* Right - Content */}
                <div className="flex-1 max-w-xl">
                  <h3 className="text-lg sm:text-xl md:text-2xl font-medium text-white tracking-[-0.02em] mb-2 sm:mb-3 md:mb-4">
                    {lang === 'en' ? 'Understand documents instantly' : 'Forstå dokumenter umiddelbart'}
                  </h3>
                  <p className="text-white/35 text-sm sm:text-base leading-relaxed">
                    {lang === 'en' 
                      ? 'Upload a contract. Get key risks, deadlines, and parties extracted in seconds. Like having a senior associate who never sleeps.'
                      : 'Last opp en kontrakt. Få nøkkelrisikoer, frister og parter ekstrahert på sekunder. Som å ha en erfaren advokat som aldri sover.'}
                  </p>
                </div>
              </div>
            </motion.div>
            
            {/* Feature 2 - Time Tracking */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="group"
            >
              <div className="flex flex-col md:flex-row md:items-start gap-4 sm:gap-6 md:gap-16">
                {/* Left - Number & Line */}
                <div className="flex items-center gap-4 sm:gap-6 md:w-48 flex-shrink-0">
                  <span className="text-white/10 text-4xl sm:text-5xl md:text-6xl font-light">02</span>
                  <div className="hidden md:block flex-1 h-px bg-gradient-to-r from-white/10 to-transparent" />
                </div>
                
                {/* Right - Content */}
                <div className="flex-1 max-w-xl">
                  <h3 className="text-lg sm:text-xl md:text-2xl font-medium text-white tracking-[-0.02em] mb-2 sm:mb-3 md:mb-4">
                    {lang === 'en' ? 'Never miss a billable hour' : 'Aldri gå glipp av en fakturerbar time'}
                  </h3>
                  <p className="text-white/35 text-sm sm:text-base leading-relaxed">
                    {lang === 'en' 
                      ? 'Lexy watches your work and suggests time entries automatically. Review and approve with one click. Capture 23% more billable time.'
                      : 'Lexy følger med på arbeidet ditt og foreslår timeregistreringer automatisk. Gjennomgå og godkjenn med ett klikk. Fang 23% mer fakturerbar tid.'}
                  </p>
                </div>
              </div>
            </motion.div>
            
            {/* Feature 3 - Smart Inbox */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="group"
            >
              <div className="flex flex-col md:flex-row md:items-start gap-4 sm:gap-6 md:gap-16">
                {/* Left - Number & Line */}
                <div className="flex items-center gap-4 sm:gap-6 md:w-48 flex-shrink-0">
                  <span className="text-white/10 text-4xl sm:text-5xl md:text-6xl font-light">03</span>
                  <div className="hidden md:block flex-1 h-px bg-gradient-to-r from-white/10 to-transparent" />
                </div>
                
                {/* Right - Content */}
                <div className="flex-1 max-w-xl">
                  <h3 className="text-lg sm:text-xl md:text-2xl font-medium text-white tracking-[-0.02em] mb-2 sm:mb-3 md:mb-4">
                    {lang === 'en' ? 'Inbox zero, effortlessly' : 'Tom innboks, uten anstrengelse'}
                  </h3>
                  <p className="text-white/35 text-sm sm:text-base leading-relaxed">
                    {lang === 'en' 
                      ? 'Every email automatically sorted, prioritized, and linked to the right matter. AI extracts action items so nothing falls through.'
                      : 'Hver e-post automatisk sortert, prioritert og koblet til riktig sak. AI trekker ut oppgaver slik at ingenting glipper.'}
                  </p>
                </div>
              </div>
            </motion.div>
            
            {/* Feature 4 - Documents */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="group"
            >
              <div className="flex flex-col md:flex-row md:items-start gap-4 sm:gap-6 md:gap-16">
                {/* Left - Number & Line */}
                <div className="flex items-center gap-4 sm:gap-6 md:w-48 flex-shrink-0">
                  <span className="text-white/10 text-4xl sm:text-5xl md:text-6xl font-light">04</span>
                  <div className="hidden md:block flex-1 h-px bg-gradient-to-r from-white/10 to-transparent" />
                </div>
                
                {/* Right - Content */}
                <div className="flex-1 max-w-xl">
                  <h3 className="text-lg sm:text-xl md:text-2xl font-medium text-white tracking-[-0.02em] mb-2 sm:mb-3 md:mb-4">
                    {lang === 'en' ? 'Draft in seconds, not hours' : 'Utkast på sekunder, ikke timer'}
                  </h3>
                  <p className="text-white/35 text-sm sm:text-base leading-relaxed">
                    {lang === 'en' 
                      ? 'Generate contracts, motions, and letters from your templates. AI personalizes every document. You just review and send.'
                      : 'Generer kontrakter, prosesskriv og brev fra dine maler. AI tilpasser hvert dokument. Du bare gjennomgår og sender.'}
                  </p>
                </div>
              </div>
            </motion.div>
            
          </div>
          
          {/* Bottom accent - Subtle */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.5 }}
            className="mt-24 pt-16 border-t border-white/[0.04]"
          >
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              <p className="text-white/25 text-sm">
                {lang === 'en' ? 'GDPR compliant · Norwegian data centers · Bank-grade encryption' : 'GDPR-kompatibel · Norske datasentre · Banknivå kryptering'}
              </p>
              <div className="flex items-center gap-8">
                {['GDPR', 'ISO 27001', 'SOC 2'].map((badge) => (
                  <span key={badge} className="text-white/15 text-xs font-medium tracking-wider">{badge}</span>
                ))}
              </div>
            </div>
          </motion.div>
          
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-28">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-indigo-400 text-xs uppercase tracking-[0.3em] font-medium mb-4"
            >
              {t.features.label}
            </motion.p>
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl md:text-5xl font-semibold text-white mb-5 tracking-tight"
            >
              {t.features.title}
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-white/35 text-base max-w-lg mx-auto"
            >
              {t.features.subtitle}
            </motion.p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-4">
            {t.features.items.map((feature, i) => {
              const icons = [FolderOpen, Users, MessageSquare, Sparkles, PenTool, Shield];
              const colors = ['#6366f1', '#8b5cf6', '#3b82f6', '#a855f7', '#06b6d4', '#10b981'];
              const Icon = icons[i];
              return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="group p-6 rounded-2xl border border-white/[0.04] bg-white/[0.01] hover:bg-white/[0.03] hover:border-white/[0.08] transition-all duration-300"
              >
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center mb-5 transition-transform group-hover:scale-110 duration-300"
                  style={{ backgroundColor: `${colors[i]}15` }}
                >
                  <Icon className="w-5 h-5" style={{ color: colors[i] }} />
                </div>
                <h3 className="text-base font-medium text-white mb-2">{feature.title}</h3>
                <p className="text-white/35 text-sm leading-relaxed">{feature.desc}</p>
              </motion.div>
            )})}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 border-y border-white/[0.03]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: '10x', label: t.stats.docAnalysis },
              { value: '50+', label: t.stats.lawFirms },
              { value: '5000+', label: t.stats.casesHandled },
              { value: lang === 'en' ? '47h' : '47t', label: t.stats.savedPerWeek },
            ].map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl md:text-5xl font-semibold text-white mb-2 tracking-tight">{stat.value}</div>
                <div className="text-white/25 text-xs">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-28">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex justify-center gap-0.5 mb-8">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
              ))}
            </div>
            <blockquote className="text-xl md:text-2xl text-white/80 font-normal leading-relaxed mb-10">
              "{t.testimonial.quote}"
            </blockquote>
            <div className="flex items-center justify-center gap-4">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-white text-sm font-medium">
                MH
              </div>
              <div className="text-left">
                <div className="text-white/70 text-sm font-medium">{t.testimonial.name}</div>
                <div className="text-white/25 text-xs">{t.testimonial.title}</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-28">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-14">
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-emerald-400 text-xs uppercase tracking-[0.3em] font-medium mb-4"
            >
              {t.pricing.label}
            </motion.p>
            <h2 className="text-4xl md:text-5xl font-semibold text-white mb-5 tracking-tight">
              {t.pricing.title}
            </h2>
            <p className="text-white/35 text-base">
              {t.pricing.subtitle}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-5 max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-8 rounded-2xl border border-white/[0.05] bg-white/[0.01] hover:border-white/[0.1] transition-colors"
            >
              <h3 className="text-lg font-medium text-white mb-1">{t.pricing.starter}</h3>
              <p className="text-white/25 text-sm mb-6">{t.pricing.starterDesc}</p>
              <div className="mb-8">
                <span className="text-4xl font-semibold text-white">{lang === 'en' ? '$' : ''}{t.pricing.starterPrice}</span>
                <span className="text-white/25 ml-1 text-sm">{t.pricing.perMonth}</span>
              </div>
              <ul className="space-y-3 mb-8">
                {t.pricing.features.starter.map((f, i) => (
                  <li key={i} className="flex items-center gap-3 text-white/40 text-sm">
                    <Check className="w-4 h-4 text-white/20" />{f}
                  </li>
                ))}
              </ul>
              <button 
                onClick={() => navigate('/lexy/login')}
                className="w-full py-3 rounded-full bg-white/[0.05] text-white/70 text-sm font-medium hover:bg-white/[0.1] transition-colors"
              >
                {t.pricing.startFree}
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="relative p-8 rounded-2xl border border-white/[0.1] bg-white/[0.02]"
            >
              <div className="absolute -top-3 left-6 px-3 py-1 bg-white text-[#08080a] rounded-full text-xs font-medium">
                {t.pricing.recommended}
              </div>
              <h3 className="text-lg font-medium text-white mb-1">{t.pricing.professional}</h3>
              <p className="text-white/25 text-sm mb-6">{t.pricing.professionalDesc}</p>
              <div className="mb-8">
                <span className="text-4xl font-semibold text-white">{lang === 'en' ? '$' : ''}{t.pricing.professionalPrice}</span>
                <span className="text-white/25 ml-1 text-sm">{t.pricing.perMonth}</span>
              </div>
              <ul className="space-y-3 mb-8">
                {t.pricing.features.professional.map((f, i) => (
                  <li key={i} className="flex items-center gap-3 text-white/40 text-sm">
                    <Check className="w-4 h-4 text-emerald-400" />{f}
                  </li>
                ))}
              </ul>
              <motion.button 
                onClick={() => navigate('/lexy/login')}
                className="w-full py-3 rounded-full bg-white text-[#08080a] text-sm font-medium"
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                {t.pricing.startTrial}
              </motion.button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-28">
        <div className="max-w-xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-semibold text-white mb-5 tracking-tight">
              {t.finalCta.title}
            </h2>
            <p className="text-white/35 text-base mb-10">
              {t.finalCta.subtitle}
              <br />
              {t.finalCta.subtitle2}
            </p>
            <motion.button
              onClick={() => navigate('/lexy/login')}
              className="group px-8 py-3.5 bg-white text-[#08080a] rounded-full text-sm font-medium inline-flex items-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {t.finalCta.button}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/[0.03] py-12">
        <div className="max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
              <Zap className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-white/60 text-sm font-medium">Lexy</span>
          </div>
          <div className="flex items-center gap-8 text-white/30 text-sm">
            <a href="#" className="hover:text-white/60 transition-colors">{t.footer.terms}</a>
            <a href="#" className="hover:text-white/60 transition-colors">{t.footer.privacy}</a>
            <a href="#" className="hover:text-white/60 transition-colors">Kontakt</a>
          </div>
          <p className="text-white/20 text-sm">© 2024 Lexy</p>
        </div>
      </footer>

      {/* Video Modal */}
      <AnimatePresence>
        {showVideoModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[100] flex items-center justify-center"
            data-testid="video-modal"
          >
            {/* Backdrop */}
            <motion.div 
              className="absolute inset-0 bg-black/90 backdrop-blur-md"
              onClick={() => setShowVideoModal(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />
            
            {/* Modal content */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="relative w-[92%] max-w-5xl z-10"
            >
              {/* Close button */}
              <button
                onClick={() => setShowVideoModal(false)}
                data-testid="close-video-modal"
                className="absolute -top-12 right-0 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors duration-300"
              >
                <X className="w-5 h-5 text-white" />
              </button>
              
              {/* Video container */}
              <div className="relative aspect-video rounded-xl md:rounded-2xl overflow-hidden bg-[#0c0c0e] border border-white/[0.1] shadow-2xl">
                <video
                  className="w-full h-full object-cover"
                  controls
                  autoPlay
                  playsInline
                >
                  <source src={DEMO_VIDEO_URL} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
              
              {/* Title below video */}
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-center text-white/40 text-sm mt-4"
              >
                {lang === 'en' ? 'Lexy Product Walkthrough' : 'Lexy Produktgjennomgang'}
              </motion.p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Demo Widget - Bottom Right - Minimalist & Elegant */}
      <AnimatePresence>
        {showDemoWidget && !widgetDismissed && !showVideoModal && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 15, scale: 0.98 }}
            transition={{ 
              duration: 1.2,
              ease: [0.22, 1, 0.36, 1]
            }}
            className="fixed bottom-4 sm:bottom-6 right-4 sm:right-6 z-[90] hidden sm:block"
            data-testid="demo-widget"
          >
            {/* Widget container - Narrow & Elegant */}
            <div className="relative group cursor-pointer" onClick={() => {
              setShowVideoModal(true);
              setShowDemoWidget(false);
            }}>
              {/* Subtle glow on hover */}
              <motion.div 
                className="absolute -inset-2 bg-violet-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"
              />
              
              {/* Main widget - Narrower, vertical format */}
              <div className="relative bg-[#0c0c0e]/95 backdrop-blur-xl border border-white/[0.06] rounded-xl overflow-hidden shadow-2xl shadow-black/60 w-[160px] sm:w-[180px]">
                
                {/* Close button - More subtle */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setWidgetDismissed(true);
                    setShowDemoWidget(false);
                  }}
                  className="absolute top-2 right-2 z-20 w-5 h-5 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center transition-all duration-300 opacity-0 group-hover:opacity-100"
                  data-testid="close-demo-widget"
                >
                  <X className="w-3 h-3 text-white/60" />
                </button>
                
                {/* Video preview - Vertical aspect ratio */}
                <div className="relative" style={{ aspectRatio: '9/12' }}>
                  <video
                    className="w-full h-full object-cover"
                    autoPlay
                    loop
                    muted
                    playsInline
                  >
                    <source src={PREVIEW_VIDEO_URL} type="video/mp4" />
                  </video>
                  
                  {/* Elegant gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0c0c0e] via-[#0c0c0e]/20 to-transparent" />
                  
                  {/* Minimalist play indicator */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <motion.div
                      initial={{ scale: 0.9, opacity: 0.7 }}
                      animate={{ scale: [0.9, 1, 0.9], opacity: [0.7, 1, 0.7] }}
                      transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                      className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center"
                    >
                      <Play className="w-4 h-4 text-white ml-0.5" fill="currentColor" />
                    </motion.div>
                  </div>
                </div>
                
                {/* Content - Ultra minimal */}
                <div className="p-3 pt-2">
                  <p className="text-white/80 text-[13px] font-medium leading-tight">
                    {lang === 'en' ? 'Watch Demo' : 'Se demo'}
                  </p>
                  <p className="text-white/30 text-[11px] mt-0.5">
                    {lang === 'en' ? '2 min' : '2 min'}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LandingLinear2;
